/**
 * Superposition d'aperçu et flèches sur les cartes produit.
 *
 * Trois usages se disputent la même zone d'image :
 *   1 — survol de la carte : montre la deuxième photo du produit
 *   2 — survol d'une pastille : montre le coloris pointé
 *   3 — appui tactile sur une pastille : bascule durablement la carte
 *
 * Un simple niveau de priorité arbitre : un usage ne peut pas écraser
 * l'affichage d'un usage plus prioritaire, et en se retirant il rend la main
 * au niveau épinglé s'il y en a un.
 *
 * L'image est superposée au carrousel du thème plutôt que d'en déplacer les
 * diapositives : les flèches et le balayage continuent de fonctionner.
 */

const NIVEAU_CARTE = 1;
const NIVEAU_PASTILLE = 2;
const NIVEAU_EPINGLE = 3;

function galerie(carte) {
  return carte && carte.querySelector('.card-gallery');
}

function superposition(carte, creer = true) {
  const g = galerie(carte);
  if (!g) return null;
  let img = g.querySelector('.cadence-apercu');
  if (!img && creer) {
    img = document.createElement('img');
    img.className = 'cadence-apercu';
    img.alt = '';
    img.loading = 'lazy';
    g.appendChild(img);
  }
  return img;
}

const Apercu = {
  montrer(carte, src, niveau) {
    if (!src) return;
    const img = superposition(carte);
    if (!img) return;
    if (Number(img.dataset.niveau || 0) > niveau) return;
    img.dataset.niveau = String(niveau);
    if (img.getAttribute('src') !== src) img.src = src;
    img.dataset.visible = 'oui';
  },

  cacher(carte, niveau) {
    const img = superposition(carte, false);
    if (!img) return;
    if (Number(img.dataset.niveau || 0) > niveau) return;
    const epingle = carte.__cadenceEpingle;
    if (epingle) {
      img.dataset.niveau = String(NIVEAU_EPINGLE);
      if (img.getAttribute('src') !== epingle) img.src = epingle;
      img.dataset.visible = 'oui';
    } else {
      img.dataset.niveau = '0';
      img.dataset.visible = 'non';
    }
  },

  epingler(carte, src) {
    carte.__cadenceEpingle = src;
    this.montrer(carte, src, NIVEAU_EPINGLE);
  },

  NIVEAU_CARTE,
  NIVEAU_PASTILLE,
  NIVEAU_EPINGLE,
};

window.CadenceApercu = Apercu;

/**
 * Survol de la carte : révèle la deuxième photo du produit.
 * La source est lue dans le carrousel du thème, où toutes les photos sont
 * déjà présentes — aucune requête supplémentaire.
 */
function brancherSurvolCarte(carte) {
  if (carte.dataset.survolBranche === 'oui') return;
  carte.dataset.survolBranche = 'oui';

  const secondePhoto = () => {
    const g = galerie(carte);
    const slides = g ? g.querySelectorAll('slideshow-slide') : [];
    const img = slides[1] && slides[1].querySelector('img');
    return img ? img.currentSrc || img.src : null;
  };

  carte.addEventListener('mouseenter', () => {
    // Pendant la navigation au carrousel, l'aperçu masquerait les photos
    // que la cliente fait justement défiler.
    if (carte.dataset.navigationCarrousel === 'oui') return;
    Apercu.montrer(carte, secondePhoto(), NIVEAU_CARTE);
  });
  carte.addEventListener('mouseleave', () => {
    // Quitter la carte remet le survol en service pour la prochaine fois.
    delete carte.dataset.navigationCarrousel;
    Apercu.cacher(carte, NIVEAU_CARTE);
  });
}

/**
 * Flèches du carrousel de carte, pilotées de bout en bout.
 *
 * Le composant du thème met bien à jour son index interne et les attributs
 * aria-hidden au clic, mais la piste ne se déplace jamais. Vérifié en
 * retirant tous nos ajouts — feuille de style désactivée, superposition
 * supprimée : le blocage subsiste, il vient du thème. Une première tentative
 * de simple recalage sur l'index du composant n'a rien changé non plus.
 *
 * On ne lui demande donc plus rien : la direction est lue sur le bouton, la
 * position courante est déduite du défilement réel de la piste, et le saut
 * est appliqué directement. Le défilement doux est tenté d'abord ; s'il n'a
 * pas bougé après 400 ms — certains contextes ne l'animent jamais — on force
 * un saut instantané pour que le clic ait toujours un effet visible.
 */
function brancherFleches(carte) {
  if (carte.dataset.flechesBranchees === 'oui') return;
  const piste = carte.querySelector('slideshow-slides');
  const controles = carte.querySelectorAll('.slideshow-control');
  if (!piste || !controles.length) return;
  carte.dataset.flechesBranchees = 'oui';

  const deplacer = (sens) => {
    const slides = [...piste.querySelectorAll('slideshow-slide')];
    if (slides.length < 2) return;

    /*
     * Pour atteindre une flèche, la souris est forcément sur la carte : le
     * survol a donc déjà posé la deuxième photo en superposition, qui cache
     * le carrousel. Le défilement avait bien lieu, mais sous une image figée.
     * On rend la main au carrousel dès le premier clic, jusqu'à ce que la
     * souris quitte la carte.
     */
    carte.dataset.navigationCarrousel = 'oui';
    Apercu.cacher(carte, NIVEAU_EPINGLE);

    const origine = piste.offsetLeft;
    const positions = slides.map((s) => s.offsetLeft - origine);
    const largeur = positions[1] - positions[0] || slides[0].offsetWidth;
    if (!largeur) return;

    const actuel = Math.round(piste.scrollLeft / largeur);
    // Boucle aux extrémités, comme le carrousel du thème.
    const cible = (actuel + sens + slides.length) % slides.length;
    const gauche = positions[cible];

    piste.scrollTo({ left: gauche, behavior: 'smooth' });
    setTimeout(() => {
      if (Math.abs(piste.scrollLeft - gauche) > 4) {
        piste.scrollTo({ left: gauche, behavior: 'instant' });
      }
    }, 400);
  };

  controles.forEach((bouton) => {
    const etiquette = (bouton.getAttribute('aria-label') || '').toLowerCase();
    // Les libellés sont traduits : on teste les deux langues du thème.
    const sens = /pr[ée]c[ée]dent|previous/.test(etiquette) ? -1 : 1;
    bouton.addEventListener('click', () => deplacer(sens));
  });
}

function brancherTout() {
  // Les flèches servent aussi au doigt : elles ne dépendent pas du survol.
  document.querySelectorAll('product-card').forEach(brancherFleches);

  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.querySelectorAll('product-card').forEach(brancherSurvolCarte);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', brancherTout);
} else {
  brancherTout();
}

// Les grilles se recomposent au filtrage et à la pagination.
new MutationObserver(brancherTout).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
